import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NotFound } from '@freelancer/404';
import { Datastore, RequestStatus } from '@freelancer/datastore';
import { User, UsersCollection } from '@freelancer/datastore/collections';
import { isDefined } from '@freelancer/utils';
import * as Rx from 'rxjs';
import { filter, map } from 'rxjs/operators';

enum UsersProfileState {
  OPEN,
  CLOSED,
  SUSPENDED,
}

@Component({
  selector: 'app-user-profile-wrapper',
  template: `
    <ng-container *flViewHeaderTemplate i18n="Profile page title">
      Profile
    </ng-container>
    <ng-container *ngIf="(userStatus$ | flAsync)?.ready">
      <ng-container [ngSwitch]="profileState$ | flAsync">
        <app-user-profile-account-closed
          *ngSwitchCase="UsersProfileState.CLOSED"
          [username]="username$ | flAsync"
        ></app-user-profile-account-closed>

        <app-user-profile *ngSwitchDefault [user$]="user$"></app-user-profile>
      </ng-container>
    </ng-container>
  `,
})
export class UserProfileWrapperComponent implements OnInit, OnDestroy {
  UsersProfileState = UsersProfileState;

  user$: Rx.Observable<User>;
  userStatus$: Rx.Observable<RequestStatus<UsersCollection>>;
  profileState$: Rx.Observable<UsersProfileState | undefined>;
  username$: Rx.Observable<string>;
  statusSubscription?: Rx.Subscription;

  constructor(
    private datastore: Datastore,
    private route: ActivatedRoute,
    private notFound: NotFound,
  ) {}

  ngOnInit() {
    // When accessing the profiles page from /u/<username> the username param
    // will be set. However the profiles page should be reachable by using /me,
    // this should show the current users profile.
    // TODO: Setup routing for /me.
    this.username$ = this.route.params.pipe(
      map(params => params.username),
      filter(isDefined),
    );

    // Get users with username in params.
    const userDocument$ = this.datastore.document<UsersCollection>(
      'users',
      this.username$,
      {
        index: 'username',
        caseInsensitive: true,
      },
    );
    this.userStatus$ = userDocument$.status$;

    this.user$ = userDocument$.valueChanges();

    this.statusSubscription = this.userStatus$.subscribe(status => {
      if (status.error) {
        this.notFound.render();
      }
    });

    this.profileState$ = this.user$.pipe(
      map(user => {
        if (!user) {
          this.notFound.render();
          return;
        }

        if (user.closed) {
          return UsersProfileState.CLOSED;
        }

        // TODO: Check if the user is suspended here.

        return UsersProfileState.OPEN;
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
  }
}
